import { AfterViewChecked, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
	AlertComponent,
	ApiService,
	ButtonComponent,
	CapitalizeSpecificWordsPipe,
	Device,
	ModalComponent,
	PopupComponent,
	PopupTargetDirective,
	SrPunctuationPipe,
	SvgIconComponent,
	StorageKeys,
	BadgeComponent,
} from '@woolworthsnz/styleguide';
import { ContextResponse, TargetedOffer } from '@woolworthsnz/trader-api';
import { ProductService } from '../../services';
import { StampComponent } from '../stamp/stamp.component';
import { AllowSubstitutesComponent } from '../allow-substitutes/allow-substitutes.component';
import { AddToTrolleyComponent } from '../add-to-trolley/add-to-trolley.component';
import { PriceComponent } from '../price/price.component';
import { PriceSingleUnitAverageComponent } from '../price-single-unit-average/price-single-unit-average.component';
import { PriceMetaComponent } from '../price-meta/price-meta.component';
import { ProductImageComponent } from '../image/image.component';
import { RouterLink } from '@angular/router';
import { FuelTagComponent } from '../fuel-tag/fuel-tag.component';
import { TagComponent } from '../tag/tag.component';
import { NgIf, NgClass } from '@angular/common';
import { ShopperNotesComponent } from '../shopper-notes/shopper-notes.component';
import { PortalModule } from '@angular/cdk/portal';
import { Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'product-stamp-list',
	templateUrl: './stamp-list.component.html',
	styleUrls: ['./stamp-list.component.scss'],
	providers: [ApiService, ProductService],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		PortalModule,
		ModalComponent,
		ShopperNotesComponent,
		NgIf,
		TagComponent,
		NgClass,
		FuelTagComponent,
		RouterLink,
		ProductImageComponent,
		PriceMetaComponent,
		PriceSingleUnitAverageComponent,
		PriceComponent,
		SvgIconComponent,
		BadgeComponent,
		AlertComponent,
		ButtonComponent,
		PopupComponent,
		AddToTrolleyComponent,
		PopupTargetDirective,
		AllowSubstitutesComponent,
		CapitalizeSpecificWordsPipe,
		SrPunctuationPipe,
	],
})
export class StampListComponent extends StampComponent implements OnInit, AfterViewChecked, OnDestroy {
	@Input() showChangedPriceTutorial = false;
	isMobileDevice = false;
	enabledChangedPriceTutorial = false;
	private destroy$: Subject<boolean> = new Subject();

	ngOnInit(): void {
		super.ngOnInit();

		this.breakPointService.device$.pipe(takeUntil(this.destroy$)).subscribe((device: Device) => {
			this.isMobileDevice = device === Device.MOBILE;
		});

		this.enabledChangedPriceTutorial = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.ChangeOrderChangedPriceTutorial
		);

		// Consider the price change tutorial acknowledged when the first popup of the tutorial is shown for the first time
		this.popupService.showPopup$.pipe(takeUntil(this.destroy$)).subscribe((id: string) => {
			if (id === 'priceChangePopupStepOne' && this.enabledChangedPriceTutorial) {
				this.acknowledgeChangedPriceTutorial();
				this.setIgnoreChangedPriceTutorialAcknowledgementToFalse();
			}
		});
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	ngAfterViewChecked(): void {
		if (
			this.showChangedPriceTutorial &&
			this.enabledChangedPriceTutorial &&
			(!this.hasAcknowledgedChangedPriceTutorial() || this.shouldIgnoreChangedPriceTutorialAcknowledgement())
		) {
			this.popupService.open('priceChangePopupStepOne');
		}
	}

	// Avoid putting logic here, all logic should come from the base class
	get badgeTextOutOfStock(): string {
		return 'Out of stock';
	}

	get showTargetedOfferNotification(): boolean {
		// if maxRedemptionQuantity is equal to the c# int32 max number(unlimited), we dont need to show this alert
		return (
			this.product.productTag?.tagType === 'IsTargetedOffer' &&
			this.product.productTag?.targetedOffer?.targetedOfferType !== TargetedOffer.TypeEnum.CategoryFuel &&
			this.product.productTag?.targetedOffer?.targetedOfferType !== TargetedOffer.TypeEnum.CategoryFood &&
			this.product.productTag?.targetedOffer?.initialMaxRedemptionCount !== 2147483647
		);
	}

	handlePriceChangeTutorialNext(): void {
		this.setIgnoreChangedPriceTutorialAcknowledgementToFalse();
		this.popupService.close('priceChangePopupStepOne');
		this.popupService.open('priceChangePopupStepTwo');
	}

	acknowledgeChangedPriceTutorial(): void {
		this.localStorageService.setItem(StorageKeys.changedPriceTutorialAcknowledged, 'true');
	}

	hasAcknowledgedChangedPriceTutorial(): boolean | undefined {
		return this.localStorageService.getItem(StorageKeys.changedPriceTutorialAcknowledged);
	}

	setIgnoreChangedPriceTutorialAcknowledgementToFalse(): void {
		this.localStorageService.setItem(StorageKeys.ignoreChangedPriceTutorialAcknowledgement, 'false');
	}

	shouldIgnoreChangedPriceTutorialAcknowledgement(): boolean | undefined {
		return this.localStorageService.getItem(StorageKeys.ignoreChangedPriceTutorialAcknowledgement);
	}
}
